import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { useLazyQuery, useMutation } from '@apollo/client';
import { EnhancedButtonStatus } from '@components/_old/EnhancedButton';
import DynamicFormDrawer from '@components/dynamic-form/DynamicFormDrawer';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { publishPersonCreated } from '@features/production/proposal/helpers/person.helpers';
// we’ll reuse the persons list to obtain code + fullName for the created id
import { DEFAULT_ERROR_TEXT } from '@utils';
import { hasIndividualType } from '@utils/helper-utils';
import { getError } from '@utils/graph-utils';

import { getBusinessPartnerPersons } from '../../services';
import { getInputs, initialValues } from './content';
import { createProposalPerson, getPolicyPersonEnums } from './queries';
import { LookupToList } from './utils';
import {
  ICreateProposalPersonResponse,
  ICreateProposalPersonVars,
  IPolicyPersonDrawerProps,
  IPolicyPersonEnumsResponse
} from '.';

import { cloneDeep, isEmpty } from 'lodash';
import DateService from 'src/core/services/DateService';

const ProposalPolicyPersonDrawer: React.FC<IPolicyPersonDrawerProps> = ({
  mainBusinessPartnerId,
  section,
  open,
  onClose,
  onSubmitSuccess,
  lineExternalCode,
  proposalId
}) => {
  const [booted, setBooted] = useState<boolean>(false);
  const [formDisabled, setFormDisabled] = useState(false);
  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();
  const [lovs, setLovs] = useState<Record<string, Record<string, string>>>({});
  const [inputsForm, setInputsForm] = useState<IDynamicFormWidgetSection[]>([]);

  const [personDetailsListResults] = useLazyQuery<IPolicyPersonEnumsResponse>(
    getPolicyPersonEnums(),
    { fetchPolicy: 'no-cache' }
  );
  const [personAction] = useMutation<
    ICreateProposalPersonResponse,
    ICreateProposalPersonVars
  >(createProposalPerson());

  const [fetchPersons] = useLazyQuery(getBusinessPartnerPersons);

  const logPrefix = `[DRAWER:${section?.toUpperCase?.() ?? 'person'}]`;

  const loadLovList = async () => {
    const result = await personDetailsListResults();

    return LookupToList(result.data);
  };

  const initialize = async () => {
    try {
      const [lovData] = await Promise.all([loadLovList()]);
      setLovs(lovData || {});
      setInputsForm(
        getInputs(initialValues, lovData, lineExternalCode, section)
      );
      setBooted(true);
    } catch {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  const isIndividualOnlyInsured =
    section === 'insured' &&
    hasIndividualType(String(lineExternalCode ?? ''));

  const submitForm = async (formValues: Record<string, any>) => {
    const [data] = normalizeDynamicFormDrawerValues(inputsForm, formValues);
    setFormDisabled(true);
    setSubmitButtonState('loading');

    try {
      const variables: ICreateProposalPersonVars = {
        mainBusinessPartnerID: mainBusinessPartnerId!,
        personInput: {
          policyPersonType: section.toUpperCase(),
          type: data.type || (isIndividualOnlyInsured ? 'INDIVIDUAL' : null),
          title: data.title || null,
          firstName: data.firstName || null,
          middleName: data.middleName || null,
          lastName: data.lastName || null,
          dateOfBirth: data.dateOfBirth
            ? DateService.formatDateBackend(data.dateOfBirth)
            : null,
          companyName: data.companyName || null,
          email: data.email,
          gender: data.gender || null,
          nationality: data.nationality
            ? String(data.nationality).slice(1)
            : null,
          occupationClass: data.occupationClass || null,
          isTaxable: data.isTaxable ?? true,
          profession: data.profession || null,
          employer: data.employer || null,
          dateOfEmployment: data.dateOfEmployment
            ? DateService.formatDateBackend(data.dateOfEmployment)
            : null,
          phoneType: data.phoneType,
          phoneNumber: data.phoneNumber,
          addressType: data.addressType || null,
          address: {
            country: data.country || null,
            zone: data.zone || null,
            city: data.city || null,
            street: data.street || null,
            building: data.building || null,
            addressDetails: data.addressDetails || null,
            addressDetailsArabic: data.addressDetailsArabic || null
          },
          passportNumber: data.passportNumber
        },
        proposalID: proposalId
      };

      const res = await personAction({ variables, errorPolicy: 'all' });

      if (isEmpty(res.errors)) {
        const newPersonId = res.data?.production?.createProposalPerson;

        toast.success(
          <ToastSuccessMessage>Person added successfully!</ToastSuccessMessage>
        );

        let label = '';
        if (newPersonId) {
          // fetch list to resolve code/fullName
          const listRes = await fetchPersons({
            variables: {
              searchKeyword: '',
              mainbusinessPartnerID: mainBusinessPartnerId
            },
            fetchPolicy: 'network-only'
          });
          const items =
            (listRes.data as any)?.salesforce?.getBusinessPartnerPersons ?? [];
          const match = items.find((p: any) => p.id === newPersonId);

          const individualName = [
            variables.personInput?.firstName ?? '',
            variables.personInput?.middleName ?? '',
            variables.personInput?.lastName ?? ''
          ]
            .filter(Boolean)
            .join(' ')
            .trim();

          label = match
            ? `${match.code} - ${match.fullName}`
            : (
                variables.personInput?.companyName ||
                individualName ||
                ''
              ).trim();

          // notify parent handler (existing behavior)
          onSubmitSuccess?.({
            id: newPersonId,
            fullName: match.fullName,
            code: match.code,
            gender: variables.personInput.gender,
            dateOfBirth: variables.personInput.dateOfBirth,
            occupationClass: variables.personInput.occupationClass,
            nationalityId: variables.personInput.nationality,
            profession: variables.personInput.profession,
            passportNumber: variables.personInput.passportNumber,
            type: variables.personInput.type
          });

          publishPersonCreated({
            id: newPersonId,
            label,
            mainBusinessPartnerId: mainBusinessPartnerId!
          });
        }

        setSubmitButtonState('success');
        setFormDisabled(false);
        onClose(); // stay on page
      } else {
        setSubmitButtonState(undefined);
        setFormDisabled(false);
        toast.error(<ToastErrorMessage>{getError(res)}</ToastErrorMessage>);
      }
    } catch (e) {
      setSubmitButtonState(undefined);
      setFormDisabled(false);
      console.error(`${logPrefix} submit error`, e);
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  const onChange = async (
    fieldName: string,
    value: any,
    sectionIndex: number,
    newValues: Record<string, any>
  ) => {
    const newLovs = cloneDeep(lovs);
    if (fieldName === 'country' && value !== 'LB') newValues.zone = null;
    if (isIndividualOnlyInsured) newValues.type = 'INDIVIDUAL';
    // Profession drives the read-only Occupation Class.
    if (fieldName === 'profession') {
      newValues.occupationClass = value
        ? lovs.occupationToClass?.[value] ?? ''
        : '';
    }
    setInputsForm(getInputs(newValues, newLovs, lineExternalCode, section));
  };

  useEffect(() => {
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!booted) return <></>;

  return (
    <DynamicFormDrawer
      title="Add Person"
      onClose={onClose}
      isOpen={open}
      sections={inputsForm}
      onSubmit={submitForm}
      submitButtonState={submitButtonState}
      disableForm={formDisabled}
      onChange={onChange}
    />
  );
};

export default ProposalPolicyPersonDrawer;
